/** Small contentEditable HTML editor with a toolbar, merge-field chips, image upload and an HTML source toggle. */
import React, { useEffect, useRef, useState } from 'react';
import { Bold, Code, Heading2, Image as ImageIcon, Italic, Link as LinkIcon, List, Underline } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';

const TB = [
  ['bold', Bold, 'Bold'], ['italic', Italic, 'Italic'], ['underline', Underline, 'Underline'],
  ['h2', Heading2, 'Heading'], ['insertUnorderedList', List, 'Bullet list'], ['link', LinkIcon, 'Link'],
];

export function HtmlEditor({ value, onChange, mergeFields = [], testId = 'html-editor' }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [source, setSource] = useState(false);
  const [src, setSrc] = useState(value || '');

  useEffect(() => { if (!source && ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || ''; }, [value, source]);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
  const exec = (cmd) => {
    ref.current?.focus();
    if (cmd === 'h2') document.execCommand('formatBlock', false, 'h2');
    else if (cmd === 'link') { const url = window.prompt('Link URL (https://…)'); if (url) document.execCommand('createLink', false, url); }
    else document.execCommand(cmd, false, null);
    emit();
  };
  const insertHtml = (h) => { ref.current?.focus(); document.execCommand('insertHTML', false, h); emit(); };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await fetch(`${API_URL}/api/staff/email-templates/images`, { method: 'POST', credentials: 'include', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      insertHtml(`<img src="${d.url}" alt="" style="max-width:100%;height:auto;border-radius:8px;display:block;margin:12px 0" />`);
      toast.success('Image added');
    } catch (e) { toast.error(e.message); }
  };
  const toggleSource = () => {
    if (source) { onChange(src); setSource(false); } else { setSrc(value || ''); setSource(true); }
  };

  return (
    <div className="rounded-lg border bg-white overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 border-b bg-gray-50 px-2 py-1.5">
        {TB.map(([cmd, Icon, title]) => <button key={cmd} type="button" title={title} disabled={source} onMouseDown={(e) => e.preventDefault()} onClick={() => exec(cmd)} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" data-testid={`${testId}-${cmd}`}><Icon className="w-4 h-4" /></button>)}
        <button type="button" title="Insert image" disabled={source} onMouseDown={(e) => e.preventDefault()} onClick={() => fileRef.current?.click()} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" data-testid={`${testId}-image`}><ImageIcon className="w-4 h-4" /></button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid={`${testId}-file`} />
        <span className="mx-1 h-5 w-px bg-gray-300" />
        {mergeFields.map((m) => <button key={m.key} type="button" title={m.label} disabled={source} onMouseDown={(e) => e.preventDefault()} onClick={() => insertHtml(`{{${m.key}}}`)} className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200 hover:bg-violet-100 disabled:opacity-30" data-testid={`${testId}-merge-${m.key}`}>{`{{${m.key}}}`}</button>)}
        <button type="button" onClick={toggleSource} className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded ${source ? 'bg-gray-900 text-white' : 'hover:bg-gray-200'}`} data-testid={`${testId}-source`}><Code className="w-3.5 h-3.5" /> HTML</button>
      </div>
      {source
        ? <textarea value={src} onChange={(e) => setSrc(e.target.value)} rows={14} className="w-full p-3 font-mono text-xs outline-none" data-testid={`${testId}-textarea`} />
        : <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onBlur={emit} className="min-h-[260px] p-3 text-sm outline-none prose prose-sm max-w-none [&_h2]:text-lg [&_h2]:font-bold [&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-violet-700 [&_a]:underline" data-testid={`${testId}-area`} />}
    </div>
  );
}
